import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Skeleton } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { CustomerDocumentsContent } from './customer-documents-content.tsx'
import { CustomerDocumentsHeader } from './customer-documents-header.tsx'
import { CustomerDocumentsNavigation } from './customer-documents-navigation.tsx'
import { CustomerIssueButton } from './customer-issue-button.tsx'
import { CustomerSendButton } from './customer-send-button.tsx'
import { getPreviousMonthBillSearch } from './search.ts'
import type { CustomerDocumentsViewProps } from './types.ts'

export function CustomerDocumentsWorkspace({
  customerId,
  currentSession,
  search,
  onSearchChange,
  canReadOrders,
  canReadBills
}: CustomerDocumentsViewProps & {
  canReadOrders: boolean
  canReadBills: boolean
}) {
  const trpc = useTRPC()
  const customer = useQuery(trpc.customer.list.queryOptions({ current: 1, pageSize: 1, id: customerId }))
  const record = customer.data?.data[0]
  return (
    <div className="bg-page text-content flex min-h-dvh flex-col">
      <CustomerDocumentsHeader
        customerId={customerId}
        customerName={record?.name}
        actions={
          record &&
          canReadBills && (
            <>
              <Button onClick={() => void onSearchChange(getPreviousMonthBillSearch(customerId))}>查看上月账单</Button>
              <CustomerSendButton
                customerId={customerId}
                customerName={record.name}
                wecomChatId={record.wecomChatId}
                disabled={record.isDisable}
              />
              {(currentSession.isSuperAdmin ||
                currentSession.permissions.some(
                  (permission) => permission.startsWith('bill.') && !permission.endsWith('.read')
                )) && <CustomerIssueButton key={customerId} customerId={customerId} />}
            </>
          )
        }
      />
      {customer.isError ? (
        <Alert
          className="m-4"
          type="error"
          showIcon
          title={customer.error.message}
          action={<Button onClick={() => void customer.refetch()}>重试</Button>}
        />
      ) : customer.isPending ? (
        <Skeleton active className="p-6" />
      ) : !record ? (
        <Alert className="m-4" type="warning" showIcon title="客户不存在或已删除" />
      ) : (
        <div className="flex min-w-0 flex-1 flex-col gap-4 p-4">
          <div className="grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-[12rem_minmax(0,1fr)]">
            <CustomerDocumentsNavigation
              kind={search.kind}
              canReadOrders={canReadOrders}
              canReadBills={canReadBills}
              onChange={(kind) => void onSearchChange({ kind })}
            />
            <CustomerDocumentsContent
              customerId={customerId}
              currentSession={currentSession}
              search={search}
              onSearchChange={onSearchChange}
            />
          </div>
        </div>
      )}
    </div>
  )
}
